/**
 * Recording a voice message.
 *
 * 1. Opening it asks for the microphone. Nothing records until that is granted.
 * 2. While recording: a pulsing dot, the elapsed time and a live level meter
 *    that moves with your voice, with Cancel and Stop.
 * 3. After Stop: the clip in the same player a received voice note uses, so
 *    you can listen back, then Record again, Discard or Add to message.
 *    Adding puts it with the pending attachments; it is sent only by Send.
 *
 * Cancel, closing, or leaving the screen stops the microphone and throws the
 * clip away. Recording stops by itself after five minutes.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { VoiceNote } from './ChatMedia';
import { Tooltip } from './Tooltip';

const MAX_MS = 5 * 60 * 1000;
const METER_BARS = 36;

type Phase = 'asking' | 'recording' | 'preview' | 'denied' | 'unsupported' | 'failed';

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const type of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm']) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return undefined;
}

function fmt(ms: number) {
  const s = Math.floor(ms / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}

const SHELL = 'mb-2 flex items-center gap-2.5 rounded-[calc(20px*var(--radius-mul,1))] bg-surface-2 p-2 shadow-[inset_0_0_0_1px_var(--line-soft)] animate-[fade-in_220ms_ease-out] motion-reduce:animate-none';

export function VoiceRecorder({ onAdd, onClose }: { onAdd: (file: File) => void; onClose: () => void }) {
  const [phase, setPhase] = useState<Phase>('asking');
  const [elapsed, setElapsed] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const blob = useRef<Blob | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const timer = useRef<number | null>(null);
  const started = useRef(0);
  const audioCtx = useRef<AudioContext | null>(null);
  const frame = useRef(0);
  const meter = useRef<HTMLSpanElement[]>([]);

  function stopMic() {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    if (frame.current) { cancelAnimationFrame(frame.current); frame.current = 0; }
    void audioCtx.current?.close().catch(() => undefined);
    audioCtx.current = null;
  }

  /* The live meter: one analyser, bar heights written straight to the DOM. */
  function startMeter(s: MediaStream) {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      audioCtx.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(s).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const history: number[] = Array.from({ length: METER_BARS }, () => 0.08);
      let last = 0;
      const tick = (t: number) => {
        frame.current = requestAnimationFrame(tick);
        if (t - last < 60) return;
        last = t;
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (const v of data) peak = Math.max(peak, Math.abs(v - 128) / 128);
        history.shift();
        history.push(Math.min(1, 0.08 + peak * 2.4));
        history.forEach((h, i) => { const el = meter.current[i]; if (el) el.style.transform = 'scaleY(' + h.toFixed(3) + ')'; });
      };
      frame.current = requestAnimationFrame(tick);
    } catch { /* the meter is a nicety; recording works without it */ }
  }

  async function start() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') { setPhase('unsupported'); return; }
    setPhase('asking');
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (err) {
      const name = (err as Error).name;
      setPhase(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'failed');
      return;
    }

    const mime = pickMime();
    const rec = new MediaRecorder(stream.current, mime ? { mimeType: mime, audioBitsPerSecond: 64000 } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      stopMic();
      const out = new Blob(chunks, { type: (rec.mimeType || 'audio/webm').split(';')[0] });
      blob.current = out;
      setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(out); });
      setPhase('preview');
    };
    recorder.current = rec;
    rec.start(250);
    started.current = Date.now();
    setElapsed(0);
    setPhase('recording');
    startMeter(stream.current);
    timer.current = window.setInterval(() => {
      const ms = Date.now() - started.current;
      setElapsed(ms);
      if (ms >= MAX_MS && rec.state === 'recording') rec.stop();
    }, 200);
  }

  function stop() {
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }

  function cancel() {
    if (recorder.current && recorder.current.state === 'recording') {
      recorder.current.onstop = null;
      recorder.current.stop();
    }
    stopMic();
    if (url) URL.revokeObjectURL(url);
    onClose();
  }

  function add() {
    if (!blob.current) return;
    const ext = blob.current.type.includes('ogg') ? 'ogg' : 'webm';
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    onAdd(new File([blob.current], 'voice-note-' + stamp + '.' + ext, { type: blob.current.type }));
    onClose();
  }

  /* Opening the recorder is the request for the microphone. */
  useEffect(() => {
    void start();
    return () => {
      if (recorder.current && recorder.current.state === 'recording') {
        recorder.current.onstop = null;
        recorder.current.stop();
      }
      stopMic();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  if (phase === 'denied' || phase === 'unsupported' || phase === 'failed') {
    return (
      <div role="alert" className={SHELL}>
        <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] text-bad"><Icon name="micOff" size={18} /></span>
        <span className="min-w-0 flex-1 text-[13.5px] text-dim">
          {phase === 'denied'
            ? 'Microphone access is blocked. Allow it for Pugo in Windows privacy settings, then try again.'
            : phase === 'unsupported'
              ? 'Voice messages are not supported here.'
              : 'The microphone could not start. Check it is connected and not in use.'}
        </span>
        {phase !== 'unsupported' ? <Pill onClick={() => void start()} icon="refresh" label="Try again" /> : null}
        <Round onClick={cancel} icon="close" label="Close" />
      </div>
    );
  }

  if (phase === 'preview' && url && blob.current) {
    return (
      <div role="group" aria-label="Voice message preview" className={cn(SHELL, 'flex-wrap')}>
        <div className="min-w-[240px] flex-1">
          <VoiceNote a={{ id: 'preview', name: 'voice-note-preview.webm', type: blob.current.type || 'audio/webm', size: blob.current.size, url, image: false }} src={url} />
        </div>
        <div className="flex flex-none items-center gap-1.5">
          <Round onClick={cancel} icon="trash" label="Discard" danger />
          <Round onClick={() => void start()} icon="mic" label="Record again" />
          <button
            type="button"
            onClick={add}
            className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-4 text-[14px] font-bold text-accent-fg outline-none transition-[background-color,transform] duration-150 hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
          >
            <Icon name="check" size={16} />
            Add to message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" className={SHELL}>
      <Round onClick={cancel} icon="trash" label="Cancel recording" danger />
      <span className="flex flex-none items-center gap-2 pl-1">
        <span className="relative flex h-3 w-3">
          {phase === 'recording' ? <span className="absolute inset-0 animate-ping rounded-full bg-bad opacity-60 motion-reduce:animate-none" /> : null}
          <span className="relative h-3 w-3 rounded-full bg-bad" />
        </span>
        <span className="w-[42px] text-[14px] font-semibold text-ink tabular-nums">{phase === 'asking' ? '0:00' : fmt(elapsed)}</span>
      </span>
      <span aria-hidden className="flex h-9 min-w-0 flex-1 items-center gap-[3px] overflow-hidden px-1">
        {Array.from({ length: METER_BARS }, (_, i) => (
          <span
            key={i}
            ref={(el) => { if (el) meter.current[i] = el; }}
            className="h-full w-[3px] flex-1 origin-center rounded-full bg-bad/70 transition-transform duration-75"
            style={{ transform: 'scaleY(.08)' }}
          />
        ))}
      </span>
      <span className="hidden flex-none text-[13px] text-mute sm:inline">{phase === 'asking' ? 'Waiting for the microphone…' : 'Recording'}</span>
      {phase === 'recording' ? (
        <Tooltip content="Stop and listen back">
          <button
            type="button"
            onClick={stop}
            aria-label="Stop recording"
            className="grid h-10 w-10 flex-none place-items-center rounded-full bg-bad text-white outline-none transition-[background-color,transform] duration-150 hover:bg-[color-mix(in_srgb,var(--bad)_84%,#fff)] active:scale-95 focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
          >
            <Icon name="stop" size={15} />
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
}

function Round({ onClick, icon, label, danger }: { onClick: () => void; icon: string; label: string; danger?: boolean }) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className={cn(
          'grid h-10 w-10 flex-none place-items-center rounded-full bg-surface-1 text-mute shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-[background-color,color] duration-200 focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          danger ? 'hover:bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] hover:text-bad' : 'hover:bg-surface-3 hover:text-ink'
        )}
      >
        <Icon name={icon} size={16} />
      </button>
    </Tooltip>
  );
}

function Pill({ onClick, icon, label }: { onClick: () => void; icon: string; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-10 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 px-3.5 text-[13.5px] font-semibold text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-colors duration-200 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
    >
      <Icon name={icon} size={15} />
      {label}
    </button>
  );
}
